/**
 * useMediaQuery Hook
 * Monitors CSS media query matches for responsive behavior
 */

import { useState, useEffect } from 'react';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // SSR fallback - assume desktop
    if (typeof window === 'undefined') return true;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQueryList = window.matchMedia(query);

    // Set initial value
    setMatches(mediaQueryList.matches);

    // Handle changes
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    }

    // Legacy browsers (Safari < 14)
    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, [query]);

  return matches;
}

// Predefined breakpoints (matching Tailwind defaults)
export const breakpoints = {
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)',
} as const;

// Convenience hooks for common breakpoints
export function useIsDesktop(): boolean {
  return useMediaQuery(breakpoints.lg);
}

export function useIsTablet(): boolean {
  return useMediaQuery(breakpoints.md);
}

export function useIsMobile(): boolean {
  return !useMediaQuery(breakpoints.sm);
}

export default useMediaQuery;
